import { StrictMode, Suspense, lazy, useEffect, type ComponentType } from 'react';
import { reloadOnceOnChunkError } from '@qs/ui/lib/chunkReload';
import { createRoot } from 'react-dom/client';
import { EnvBanner } from '@qs/ui/EnvBanner';
import { Navigate, Outlet, RouterProvider, createBrowserRouter, useRouteError } from 'react-router';
import '@qs/ui/global.css';
import { RequireMember, StudioAuthProvider } from './lib/auth';

// スタジオ（問題管理アプリ）。docs/studio.md。主催者のアプリとはログインも画面も別
function page<M extends Record<string, unknown>>(load: () => Promise<M>, name: keyof M) {
  const C = lazy(async () => ({ default: (await load())[name] as ComponentType }));
  return <C />;
}
const account = () => import('./pages/Account');
const ops = () => import('./pages/ops');

const member = (load: () => Promise<Record<string, unknown>>, name: string, admin = false) => <RequireMember admin={admin}>{page(load, name)}</RequireMember>;

function Loading() {
  return <div style={{ padding: 32, color: 'var(--color-ink-muted)' }}>読み込み中…</div>;
}

function AppError() {
  const err = useRouteError();
  // 部品の読み込みの失敗（更新のあとなど）は、1回だけ自動で読み込み直す
  useEffect(() => {
    reloadOnceOnChunkError(err);
  }, [err]);
  return (
    <div style={{ padding: 32, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <h1 style={{ font: 'var(--type-heading-2)', margin: 0 }}>画面を表示できませんでした</h1>
      <small>{err instanceof Error ? err.message : String(err)}</small>
      <button type="button" onClick={() => location.reload()}>
        再読み込み
      </button>
    </div>
  );
}

function NotFound() {
  return (
    <div style={{ padding: 48 }}>
      <h1 style={{ font: 'var(--type-heading-1)' }}>ページが見つかりません</h1>
      <a href="/">ホームへ</a>
    </div>
  );
}

const router = createBrowserRouter([
  {
    errorElement: <AppError />,
    element: (
      <StudioAuthProvider>
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </StudioAuthProvider>
    ),
    children: [
      { path: '/login', element: page(account, 'LoginPage') },
      { path: '/invite', element: page(account, 'InvitePage') },
      // 5つのメニュー（docs/studio-automation.md 6章）
      { path: '/', element: member(ops, 'TodoPage') },
      { path: '/packs', element: member(ops, 'AdminPacksPage', true) },
      { path: '/packs/trash', element: member(ops, 'AdminPackTrashPage', true) },
      { path: '/packs/:id', element: member(ops, 'AdminPackEditPage', true) },
      { path: '/packs/:id/store', element: member(ops, 'AdminPackEditPage', true) },
      { path: '/packs/:id/jobs', element: member(ops, 'AdminJobsPage', true) },
      { path: '/packs/:id/jobs/new', element: member(ops, 'AdminJobNewPage', true) },
      { path: '/packs/:id/jobs/:jobId', element: member(ops, 'AdminJobPage', true) },
      { path: '/packs/:id/review', element: member(ops, 'ReviewPage', true) },
      { path: '/packs/:id/audio', element: member(ops, 'ReviewPage', true) },
      { path: '/packs/:id/pool', element: member(ops, 'PackPoolPage', true) },
      // クイズの詳細（2026-10-10）：収録クイズから・作問ツールから
      { path: '/packs/:id/pool/:qid', element: member(ops, 'PackQuizPage', true) },
      { path: '/packs/:id/make/:qid', element: member(ops, 'PackQuizPage', true) },
      { path: '/tasks', element: member(ops, 'TasksPage', true) },
      { path: '/inbox', element: member(ops, 'InboxPage', true) },
      { path: '/analytics', element: member(ops, 'AnalyticsPage', true) },
      { path: '/review', element: member(ops, 'ReviewPage') },
      { path: '/settings', element: member(ops, 'SettingsPage') },
      { path: '/members', element: <Navigate to="/system/users" replace /> },
      { path: '/system', element: member(ops, 'SystemPage', true) },
      { path: '/system/users', element: member(ops, 'SystemUsersPage', true) },
      { path: '/system/settings', element: member(ops, 'SystemSettingsPage', true) },
      { path: '/system/status', element: member(ops, 'SystemStatusPage', true) },
      { path: '/system/tournament', element: member(ops, 'TournamentDefaultsPage', true) },
      { path: '/system/help', element: member(ops, 'SystemHelpPage', true) },
      { path: '/masters', element: member(ops, 'MastersPage', true) },
      { path: '/masters/genres', element: member(ops, 'SystemGenresPage', true) },
      { path: '/masters/audiences', element: member(ops, 'AudiencesPage', true) },
      { path: '/system/api-usage', element: member(ops, 'ApiUsagePage', true) },
      { path: '/system/gemini-key', element: member(ops, 'GeminiKeyPage', true) },
      { path: '/system/genres', element: <Navigate to="/masters/genres" replace /> },
      // 「詳細」はなくした（制作・運営の画面は左のメニューへ。DECISIONS.md 2026-10-08）
      { path: '/detail', element: <Navigate to="/questions" replace /> },
      // 詳細の中（問題・作問ジョブ・指示書・声・売上・操作の記録）
      { path: '/questions', element: member(ops, 'AdminQuestionsPage') },
      { path: '/questions/:id', element: member(ops, 'AdminQuestionEditPage') },
      { path: '/questions/:id/audio', element: member(ops, 'AdminAudioPage') },
      { path: '/jobs', element: member(ops, 'AdminJobsPage', true) },
      { path: '/jobs/new', element: member(ops, 'AdminJobNewPage', true) },
      { path: '/jobs/feedback', element: member(ops, 'FeedbackPage', true) },
      { path: '/jobs/:id', element: member(ops, 'AdminJobPage', true) },
      { path: '/prompts', element: member(ops, 'AdminPromptsPage', true) },
      { path: '/voices', element: member(ops, 'AdminVoicesPage', true) },
      { path: '/sales', element: member(ops, 'AdminSalesPage', true) },
      { path: '/audit', element: member(account, 'AuditPage', true) },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <EnvBanner />
    <RouterProvider router={router} />
  </StrictMode>,
);
